import { ReactNode, useEffect, useRef } from 'react';

import { Field } from '@components/form/dynamic-fields';
import { FormV2 } from '@components/form/v2';
import { RouterTab } from '@components/header/router-tabs-header';
import { FieldMappingGroup } from '@components/text-editors/addons/field-mapping';

import { PredefinedOptionsLists } from './context';
import { FieldConfig, clearRegistry, register } from './field-registry';
import { contentFileConfig } from './fields/content-file';
import { dateConfig } from './fields/date';
import { dropdownConfig } from './fields/dropdown';
import { emailConfig } from './fields/email';
import { fileConfig } from './fields/file';
import { headingConfig } from './fields/heading';
import { htmlConfig } from './fields/html';
import { imageConfig } from './fields/image';
import { kennitalaConfig } from './fields/kennitala';
import { multipleChoiceConfig } from './fields/multiple-choice';
import { numberConfig } from './fields/number';
import { phoneConfig } from './fields/phone';
import { sectionConfig } from './fields/section';
import { separatorConfig } from './fields/separator';
import { singleChoiceConfig } from './fields/single-choice';
import { subheadingConfig } from './fields/subheading';
import { submitConfig } from './fields/submit';
import { textLargeField } from './fields/text-large';
import { textSmallField } from './fields/text-small';
import { videoConfig } from './fields/video';
import { FormBuilderState } from './state';
import { FormBuilderRouterTop, FormBuilderTabsTop } from './top';

export type OnImageSaveProps = {
  data: FormData;
  setProgress?: (progress: number) => void;
};

export type OnFileSaveProps = {
  data: FormData;
  setProgress?: (progress: number) => void;
};

export type OnFileSaveResult = {
  fileId: string;
  fileName: string;
  url: string;
  urlConverted?: string;
};

export type EditedTemplateData = {
  fieldsConfig: FormV2;
  name: string;
  description: string;
  submissionMessageConfig: FormV2;
  removedImages: string[];
  removedFiles: string[];
};

export type FormBuilderProps = {
  form?: FormV2 | Field[];
  submissionMessage?: FormV2;
  name?: string;
  description?: string;
  showKennitala?: boolean;
  /**
   *
   * @param template Fields list and template name to be saved
   * @param onSaveSuccess Callback to reset the diff state after saving
   */
  onSave(
    template: Partial<EditedTemplateData>,
    onSaveSuccess?: (overrideForm?: FormV2, overrideSubmissionMessage?: FormV2) => void,
    createNavigationString?: string
  ): Promise<void>;
  backUrl?: string;
  predefinedOptionsLists?: PredefinedOptionsLists;
  extraTabs?: RouterTab[];
  extraTopActions?: ReactNode;
  extraFieldGroups?: ReactNode;
  /**
   * Actions group below the main actions
   */
  secondaryActions?: ReactNode;
  fields?: Array<FieldConfig<any>>;
  variableGroups?: FieldMappingGroup[];
  // Set if the current template hasn't been saved once, since
  // that changes behavior around saving inside the form builder.
  isCreating?: boolean;
  onImageSave?: (props: OnImageSaveProps) => Promise<string>;
  onFileSave?: (props: OnFileSaveProps) => Promise<OnFileSaveResult>;
  useTabs?: boolean;
  onComplete?: () => void;
  formVariant?: FormBuilderState['formVariant'];
  mode?: FormBuilderState['mode'];
};

export const defaultFormBuilderFields = [
  submitConfig,

  textSmallField,
  textLargeField,
  kennitalaConfig,
  numberConfig,
  dateConfig,
  fileConfig,
  phoneConfig,
  emailConfig,

  sectionConfig,
  separatorConfig,

  multipleChoiceConfig,
  singleChoiceConfig,
  dropdownConfig,

  headingConfig,
  subheadingConfig,
  htmlConfig,
  videoConfig,
  imageConfig,
  contentFileConfig,
] satisfies Array<FieldConfig<any>>;

export const FormBuilder = ({
  formVariant = 'input',
  predefinedOptionsLists = [],
  variableGroups = [],
  showKennitala = false,
  fields = defaultFormBuilderFields.filter(
    (field) => field.kind !== 'input-kennitala' || showKennitala
  ),
  useTabs = false,
  ...rest
}: FormBuilderProps) => {
  const registeredRef = useRef(false);

  if (!registeredRef.current) {
    fields.forEach(register);
    registeredRef.current = true;
  }
  useEffect(() => {
    return () => {
      clearRegistry();
    };
  }, []);

  return useTabs ? (
    <FormBuilderTabsTop
      predefinedOptionsLists={predefinedOptionsLists}
      variableGroups={variableGroups}
      {...rest}
      formVariant={formVariant}
    />
  ) : (
    <FormBuilderRouterTop
      predefinedOptionsLists={predefinedOptionsLists}
      variableGroups={variableGroups}
      {...rest}
      formVariant={formVariant}
    />
  );
};
